// Project → Purchase orders (other UI redesign, handoff W15 list).
//
// Hits /projects/:id/purchase-orders, which lists every PO for the project's
// orders (supplier COST only — a supplier-facing record never carries the
// client's resale price). Read-only: POs are created in ONE place, the Sales
// Order's "Create purchase orders" step.
//
// Blanks + decorator chains (two dependent POs) are shown together: the blank-
// goods PO, then its decoration PO indented under it, with a visible "blanks
// not arrived" flag until the blank PO reaches delivered (pairedPoStage comes
// from the API — services/purchase-orders.ts). A decorated-goods supplier PO
// stands alone.
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Send } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/ui/StatusPill";
import { TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { cn, timeAgo } from "@/lib/utils";
import { blanksArrived, formatShortDate, poStageLabel, poStageTone } from "@/lib/production";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

interface PoRow {
  id: string;
  poNumber: number;
  orderId: string;
  orderNumber: number;
  vendorId: string;
  vendorName: string;
  poType: string;
  internalStage: string;
  exceptionFlag: boolean;
  followUpFlag: boolean;
  subtotalCost: string;
  requiredShipDate: string | null;
  supplierShipDate: string | null;
  sentAt: string | null;
  createdAt: string | null;
  pairedPoId: string | null;
  pairedPoNumber: number | null;
  pairedPoStage: string | null;
}

interface PosListResponse {
  rows: PoRow[];
}

function formatMoney(s: string): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function roleLabel(p: PoRow): string {
  if (p.poType === "decoration_service") return "Decoration";
  return p.pairedPoId && p.internalStage !== "cancelled" ? "Blank goods → decorator" : "Goods";
}

/** Order the rows so each decoration PO sits right under its blank-goods PO. */
function chainOrder(rows: PoRow[]): Array<{ row: PoRow; child: boolean }> {
  const out: Array<{ row: PoRow; child: boolean }> = [];
  const placed = new Set<string>();
  const sorted = [...rows].sort((a, b) => a.orderNumber - b.orderNumber || a.poNumber - b.poNumber);
  for (const r of sorted) {
    if (placed.has(r.id)) continue;
    // A live decoration PO is placed under its supplier PO (when present here).
    if (
      r.poType === "decoration_service" &&
      r.pairedPoId &&
      r.internalStage !== "cancelled" &&
      rows.some((o) => o.id === r.pairedPoId)
    )
      continue;
    out.push({ row: r, child: false });
    placed.add(r.id);
    if (r.poType === "product_supply" && r.pairedPoId && r.internalStage !== "cancelled") {
      const deco = rows.find((o) => o.id === r.pairedPoId);
      if (deco && !placed.has(deco.id)) {
        out.push({ row: deco, child: true });
        placed.add(deco.id);
      }
    }
  }
  for (const r of sorted) if (!placed.has(r.id)) out.push({ row: r, child: false });
  return out;
}

export function ProjectPosTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const navigate = useNavigate();
  const projectId = data.project.id;
  // PO idea 2 (Stephanie 2026-10-15): once every PO is created you land here
  // with ?new=<ids>; those rows say "New", and each PO not yet sent has an
  // "Email to supplier" button that opens it with the Send window open.
  const [searchParams] = useSearchParams();
  const newIds = new Set((searchParams.get("new") ?? "").split(",").filter(Boolean));
  const { user } = useAuth();
  const canSend = !!user && (user.roles.includes("admin") || user.roles.includes("production_rep"));

  const { data: list, isLoading, isError } = useQuery({
    queryKey: ["project", projectId, "purchase-orders"],
    queryFn: () =>
      api<PosListResponse>(`/projects/${projectId}/purchase-orders`),
  });

  const rows = list?.rows ?? [];
  // Row-grounded sales-order count (rail node) — POs are spawned from the Sales
  // Order in production handoff, so the empty state points there when one exists.
  const salesOrderCount =
    data.stages.find((s) => s.key === "sales_order")?.count ?? 0;
  const live = rows.filter((r) => r.internalStage !== "cancelled");
  const waitingBlanks = live.filter(
    (r) => r.poType === "decoration_service" && r.pairedPoId && !blanksArrived(r.pairedPoStage) && !["shipped", "delivered", "billed", "closed"].includes(r.internalStage),
  ).length;
  const flagged = live.filter((r) => r.exceptionFlag).length;
  const drafts = live.filter((r) => r.internalStage === "draft").length;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Purchase orders</h2>
        <p className="text-xs text-muted">
          {rows.length} {rows.length === 1 ? "PO" : "POs"}
          {drafts > 0 && ` · ${drafts} not sent`}
          {flagged > 0 && ` · ${flagged} flagged`}
          {waitingBlanks > 0 && ` · ${waitingBlanks} waiting on blanks`}
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-md border border-rule bg-white p-10 text-center text-sm text-muted">
          Loading purchase orders…
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-5 text-sm text-risk">
          We couldn't load purchase orders. Nothing has changed — refresh to try again.
        </div>
      ) : rows.length === 0 ? (
        salesOrderCount > 0 ? (
          // One-home-per-action (2026-07-26): purchase orders are created in ONE
          // place — the Quote's "Create purchase orders" next step. This
          // tab does NOT duplicate that action (which caused the create ↔ list
          // ping-pong); it names the exact button + links straight to the SO, so
          // the rep knows where creation happens and isn't bounced in a loop.
          <div className="rounded-md border border-rule bg-white px-5 py-12 text-center text-sm text-muted">
            <p className="font-medium text-ink">No purchase orders yet.</p>
            {/* POs are made under the Shipping plan since 2026-10-13; this
                pointed at the old Sales Order (Stephanie 2026-10-27). */}
            <p className="mt-1">
              Purchase orders are created on the Shipping tab — fill in the shipping plan, then use{" "}
              <span className="font-medium text-ink">Create purchase orders</span> under it.{" "}
              <Link
                to={`/projects/${projectId}/shipping`}
                className="font-medium text-ink underline underline-offset-4 hover:text-muted"
              >
                Open Shipping
              </Link>
            </p>
          </div>
        ) : (
          // No Quote yet — honest explain state (nothing to hand off).
          <div className="rounded-md border border-rule bg-white px-5 py-12 text-center text-sm text-muted">
            <p className="font-medium text-ink">No purchase orders yet.</p>
            <p className="mt-1">POs appear here once a quote is converted and handed to production.</p>
          </div>
        )
      ) : (
        <div className={TABLE_WRAP} role="region" aria-label="Purchase orders" tabIndex={0}>
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                <th scope="col" className={TH}>Purchase order</th>
                <th scope="col" className={TH}>Stage</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Ship by</th>
                <th scope="col" className={`${TH} hidden text-right md:table-cell`}>Cost</th>
                <th scope="col" className={`${TH} hidden lg:table-cell`}>Updated</th>
                {canSend && <th scope="col" className={TH}><span className="sr-only">Email</span></th>}
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {chainOrder(rows).map(({ row: p, child }) => {
                const isDeco = p.poType === "decoration_service";
                const live = p.internalStage !== "cancelled";
                const blanksPending =
                  isDeco && live && p.pairedPoId !== null && !blanksArrived(p.pairedPoStage) &&
                  !["shipped", "delivered", "billed", "closed"].includes(p.internalStage);
                return (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/projects/${projectId}/pos/${p.id}`)}
                    className={cn(TROW, "cursor-pointer", child && "bg-[#fcfcfa]")}
                  >
                    <td className={cn(TD, child && "pl-9 sm:pl-10")}>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {child && <span aria-hidden className="text-muted">↳</span>}
                        <span className="font-medium text-ink">PO #{p.poNumber}</span>
                        {newIds.has(p.id) && <StatusPill tone="info">New</StatusPill>}
                        <span className="text-muted">· {p.vendorName}</span>
                        {p.exceptionFlag && <StatusPill tone="warn">Exception</StatusPill>}
                        {p.followUpFlag && <StatusPill tone="info">Follow-up</StatusPill>}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-muted">
                        {roleLabel(p)} · order #{p.orderNumber}
                      </span>
                      {p.pairedPoId &&
                        p.pairedPoNumber != null &&
                        // F3 — a cancelled row is out of the chain; suppress its
                        // paired link (the pairing may be stale/nulled).
                        p.internalStage !== "cancelled" && (
                        // D4 — two-leg chain link, shown on BOTH the supplier and
                        // the decoration row (Covered-by-PO link grammar).
                        <Link
                          to={`/projects/${projectId}/pos/${p.pairedPoId}`}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-0.5 inline-block text-[11px] text-ink underline underline-offset-2 hover:text-muted"
                        >
                          Paired with PO #{p.pairedPoNumber} →
                        </Link>
                      )}
                      {isDeco && live && (
                        <span className={cn("mt-1 block text-[11px]", blanksPending ? "font-medium text-warn" : "text-muted")}>
                          {p.pairedPoId === null
                            ? "Blank goods PO not linked"
                            : blanksArrived(p.pairedPoStage)
                              ? "Blanks arrived at the decorator"
                              : `Blanks not arrived · PO #${p.pairedPoNumber} is ${poStageLabel(p.pairedPoStage ?? "").toLowerCase()}`}
                        </span>
                      )}
                    </td>
                    <td className={TD}>
                      <StatusPill tone={poStageTone(p.internalStage)}>
                        {poStageLabel(p.internalStage)}
                      </StatusPill>
                    </td>
                    <td className={`${TD} hidden sm:table-cell`}>
                      {formatShortDate(p.supplierShipDate ?? p.requiredShipDate)}
                      {p.supplierShipDate && p.requiredShipDate && p.supplierShipDate !== p.requiredShipDate && (
                        <span className="block text-[11px] text-muted">Required {formatShortDate(p.requiredShipDate)}</span>
                      )}
                    </td>
                    <td className={`${TD} hidden text-right tabular-nums md:table-cell`}>
                      {formatMoney(p.subtotalCost)}
                    </td>
                    <td className={`${TD} hidden text-muted lg:table-cell`}>
                      {timeAgo(p.sentAt ?? p.createdAt)}
                    </td>
                    {canSend && (
                      <td className={`${TD} text-right`}>
                        {p.internalStage === "draft" && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/projects/${projectId}/pos/${p.id}?send=1`);
                            }}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-700"
                          >
                            <Send className="h-3.5 w-3.5" aria-hidden /> Email to supplier
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
